import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquareReply, X, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const chime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [660, 880].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      const t = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.08, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.connect(g).connect(ctx.destination);
      o.start(t);
      o.stop(t + 0.4);
    });
  } catch (e) { /* audio blocked until user interacts */ }
};

export const ReplyAlerts = () => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const seen = useRef(new Set());

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/outreach/reply-alerts");
      if (data.some((a) => !seen.current.has(a.id))) chime();
      data.forEach((a) => seen.current.add(a.id));
      setAlerts(data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (user?.role !== "admin") return undefined;
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, [user, poll]);

  const dismiss = async (id) => {
    setAlerts((a) => a.filter((x) => x.id !== id));
    await api.post(`/outreach/reply-alerts/${id}/dismiss`).catch(() => {});
  };

  const open = (a) => {
    window.open(`/app/leads/${a.lead_id}#replies`, "_blank", "noopener");
    dismiss(a.id);
  };

  if (!alerts.length) return null;
  return (
    <div data-testid="reply-alerts" className="fixed bottom-4 right-4 left-4 sm:left-auto sm:w-96 z-[60] space-y-3">
      {alerts.slice(0, 3).map((a) => (
        <div key={a.id} data-testid={`reply-alert-${a.id}`} className="rounded-xl border border-sky-500/40 bg-[#131C31]/95 backdrop-blur-md shadow-2xl p-4 animate-fade-in">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-sm font-bold text-white inline-flex items-center gap-2">
              <MessageSquareReply className="w-4 h-4 text-sky-300" /> New reply: {a.company_name}
            </p>
            <button data-testid={`reply-alert-close-${a.id}`} onClick={() => dismiss(a.id)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-slate-400 mb-1">{a.contact_name || a.from} · {a.subject}</p>
          <p className="text-sm text-slate-300 line-clamp-2 mb-3">{a.snippet}</p>
          <div className="flex gap-2">
            <button data-testid={`reply-alert-open-${a.id}`} onClick={() => open(a)} className="btn-primary rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Open reply</button>
            <button data-testid={`reply-alert-dismiss-${a.id}`} onClick={() => dismiss(a.id)} className="rounded-lg px-3 py-1.5 text-xs border border-slate-700 text-slate-300 hover:border-slate-500">Dismiss</button>
          </div>
        </div>
      ))}
      {alerts.length > 3 && <p className="text-xs text-slate-400 text-right">+{alerts.length - 3} more replies</p>}
    </div>
  );
};
